import '@aab/sc-styles-fonts/sc-styles-font-roboto-condensed.css';
import '@aab/sc-styles-fonts/sc-styles-font-roboto.css';
import { ErrorBoundaryFallback } from '@components/ErrorBoundaryFallback';
import { Layout } from '@components/Layout';
import queryClient from '@services/queryClient';
import { AccountSelectionProvider, AuthProvider } from '@stores';
import { QueryClientProvider } from '@tanstack/react-query';
import { LocaleProvider, Spinner } from '@uikit';
import { getLocale } from '@utils';
import { FC, lazy, Suspense, useEffect, useMemo, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { I18nextProvider } from 'react-i18next';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import i18nInstance from './i18n';
import './main.scss';

const AccountSelection = lazy(
  () => import('./pages/AccountSelection/AccountSelection')
);
const Overview = lazy(() => import('./pages/Overview/Overview'));
const Summary = lazy(() => import('./pages/Summary/Summary'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

const routes = createBrowserRouter([
  {
    path: '/',
    element: <AccountSelection />,
    ErrorBoundary: ErrorBoundaryFallback
  },
  {
    path: `/${i18nInstance.t('routes.realtimePrice')}`,
    element: <Overview />,
    ErrorBoundary: ErrorBoundaryFallback
  },
  {
    path: `/${i18nInstance.t('routes.success')}`,
    element: <Summary />,
    ErrorBoundary: ErrorBoundaryFallback
  },
  {
    path: '*',
    element: <NotFound />,
    ErrorBoundary: ErrorBoundaryFallback
  }
]);

const validOrigins = {
  'ib-retail': 'ib-retail',
  'ib-business': 'ib-business'
} as const;

const App: FC = () => {
  const originRef = useRef<string>();
  const { locale } = useMemo(() => getLocale(), []);

  useEffect(() => {
    document.title = i18nInstance.t('pageTitle');
  }, [i18nInstance.t]);

  useEffect(() => {
    const origin = window.location.hash.replace('#', '');

    if (
      validOrigins[origin as keyof typeof validOrigins] &&
      !originRef.current
    ) {
      sessionStorage.setItem('origin', origin);
      originRef.current = origin;
    }
  }, []);

  return (
    <I18nextProvider i18n={i18nInstance}>
      <QueryClientProvider client={queryClient}>
        <ErrorBoundary fallback={<ErrorBoundaryFallback />}>
          <AuthProvider>
            <Layout>
              <Suspense fallback={<Spinner hideOverlay={false} />}>
                <LocaleProvider locale={locale}>
                  <AccountSelectionProvider>
                    <RouterProvider router={routes} />
                  </AccountSelectionProvider>
                </LocaleProvider>
              </Suspense>
            </Layout>
          </AuthProvider>
        </ErrorBoundary>
      </QueryClientProvider>
    </I18nextProvider>
  );
};

export default App;
